import { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { useStore } from './store/useStore';
import CustomerMenu from './components/CustomerMenu';
import AdminDashboard from './components/AdminDashboard';
import AdminLogin from './components/AdminLogin';
import AdminSignup from './components/AdminSignup';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Paper,
  Stack,
  Button,
} from '@mui/material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import { Restaurant, ArrowBack } from '@mui/icons-material';

// === LUXURY THEME ===
const theme = createTheme({
  palette: {
    primary: { main: '#d4af37' },
    secondary: { main: '#b8932b' },
    background: { default: '#f8f8f8', paper: '#ffffff' },
    text: { primary: '#1a1a1a', secondary: '#555555' },
    success: { main: '#28a745' },
    error: { main: '#c62828' },
  },
  typography: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    h3: { fontWeight: 800 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  shape: { borderRadius: 28 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          padding: '12px 28px',
          fontSize: '1rem',
          boxShadow: '0 6px 16px rgba(212,175,55,0.3)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 24px rgba(212,175,55,0.4)',
          },
        },
      },
    },
  },
});

// === GOLD TEXT ===
const GoldText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(90deg, #d4af37, #b8932b)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  fontWeight: 800,
}));

// === LUXURY CARD ===
const LuxuryCard = styled(Paper)(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid rgba(212,175,55,0.3)',
  borderRadius: 28,
  padding: '40px 32px',
  boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 6,
    background: 'linear-gradient(90deg, #d4af37, #b8932b)',
    borderRadius: '28px 28px 0 0',
  },
}));

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('admin_token');
  return token ? children : <Navigate to="/admin/login" replace />;
}

function PublicRoute({ children }) {
  const token = localStorage.getItem('admin_token');
  return token ? <Navigate to="/admin" replace /> : children;
}

function CustomerRoute({ children }) {
  const { restaurantId, tableId, token } = useStore();
  return restaurantId && tableId && token ? children : <Navigate to="/" replace />;
}

function App() {
  const { setCustomerContext } = useStore();

  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const secret = params.get('s');
    const match = path.match(/\/r\/(\d+)\/t\/(\d+)/);

    if (match && secret) {
      const [, rid, tid] = match;
      setCustomerContext(rid, tid, secret, '');
    }
  }, [setCustomerContext]);

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <Routes>
          {/* Customer Menu - Always accessible via QR */}
          <Route
            path="/r/:restaurantId/t/:tableId"
            element={<CustomerMenu />}
          />

          {/* Admin Routes */}
          <Route
            path="/admin/login"
            element={<PublicRoute><AdminLogin /></PublicRoute>}
          />
          <Route
            path="/admin/signup"
            element={<PublicRoute><AdminSignup /></PublicRoute>}
          />
          <Route
            path="/admin"
            element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>}
          />

          {/* Root Redirect */}
          <Route path="/" element={<HomeRedirect />} />

          {/* 404 Page */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

// === HOME REDIRECT WITH LOADING ===
function HomeRedirect() {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate(token ? '/admin' : '/admin/login', { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [navigate, token]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8f8f8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <CircularProgress size={60} thickness={5} color="primary" />
    </Box>
  );
}

// === LUXURY 404 PAGE ===
function NotFound() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8f8f8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="sm">
        <LuxuryCard elevation={0}>
          <Stack spacing={4} alignItems="center" textAlign="center">
            <Restaurant sx={{ fontSize: 80, color: '#d4af37' }} />
            
            <Box>
              <GoldText variant="h3" sx={{ fontSize: '3.5rem', mb: 1 }}>
                404
              </GoldText>
              <Typography variant="h5" color="text.primary" fontWeight={700} gutterBottom>
                Page Not Found
              </Typography>
              <Typography variant="body1" color="text.secondary">
                The page at <strong>{location.pathname}</strong> does not exist.
              </Typography>
            </Box>

            <Stack direction="row" spacing={2} mt={2}>
              <Button
                variant="contained"
                startIcon={<ArrowBack />}
                onClick={() => navigate(-1)}
                sx={{
                  bgcolor: '#28a745',
                  '&:hover': { bgcolor: '#218838' },
                }}
              >
                Go Back
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/')}
                sx={{
                  borderColor: '#d4af37',
                  color: '#d4af37',
                  '&:hover': {
                    borderColor: '#b8932b',
                    color: '#b8932b',
                    bgcolor: 'rgba(212,175,55,0.05)',
                  },
                }}
              >
                Home Page
              </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary" mt={4}>
              © 2025 Your Restaurant • Powered by GoldPOS
            </Typography>
          </Stack>
        </LuxuryCard>
      </Container>
    </Box>
  );
}

export default App;